import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Mountain,
  Plus, 
  MoreVertical, 
  Edit2, 
  Trash2,
  User,
  Target,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Filter,
  X,
  ChevronDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Status configuration
const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-slate-400', bgColor: 'bg-slate-50', textColor: 'text-slate-700' },
  green: { label: 'On Track', color: 'bg-green-500', bgColor: 'bg-green-50', textColor: 'text-green-700' },
  yellow: { label: 'At Risk', color: 'bg-yellow-500', bgColor: 'bg-yellow-50', textColor: 'text-yellow-700' },
  red: { label: 'Off Track', color: 'bg-red-500', bgColor: 'bg-red-50', textColor: 'text-red-700' },
  completed: { label: 'Completed', color: 'bg-blue-500', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
};

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

const QuarterlyRocks = () => {
  const { user, isAdmin, isELT } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  
  // Data state
  const [rocks, setRocks] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [goals, setGoals] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Filter state
  const currentYear = new Date().getFullYear();
  const currentQuarter = `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`;
  const [filters, setFilters] = useState({
    year: parseInt(searchParams.get('year')) || currentYear,
    quarter: searchParams.get('quarter') || '',
    pillar_id: searchParams.get('pillar') || '',
    status: searchParams.get('status') || '',
    owner_id: searchParams.get('owner') || ''
  });
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
  
  // Dialog state
  const [rockDialogOpen, setRockDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  
  // Form state
  const [editingRock, setEditingRock] = useState(null);
  const [rockToDelete, setRockToDelete] = useState(null);
  const [rockToUpdateStatus, setRockToUpdateStatus] = useState(null);
  
  const [rockForm, setRockForm] = useState({
    name: '',
    description: '',
    owner_id: '',
    department: '',
    pillar_id: '',
    annual_goal_id: '',
    year: currentYear,
    quarter: currentQuarter,
    status: 'not_started',
    percent_complete: 0,
    risks_blockers: ''
  });
  
  const [saving, setSaving] = useState(false);

  // Fetch all data
  const fetchRocks = useCallback(async () => {
    try {
      const params = { year: filters.year };
      if (filters.quarter) params.quarter = filters.quarter;
      if (filters.pillar_id) params.pillar_id = filters.pillar_id;
      if (filters.status) params.status = filters.status;
      if (filters.owner_id) params.owner_id = filters.owner_id;
      
      const response = await axios.get(`${API}/strategy/rocks`, { params });
      setRocks(response.data);
    } catch (error) {
      console.error('Failed to fetch rocks:', error);
      toast.error('Failed to load rocks');
    }
  }, [filters]);

  const fetchSupportingData = useCallback(async () => {
    try {
      const [pillarsRes, goalsRes, usersRes, deptsRes] = await Promise.all([
        axios.get(`${API}/strategy/pillars`),
        axios.get(`${API}/strategy/goals`, { params: { year: filters.year } }),
        axios.get(`${API}/strategy/rock-owners`),
        axios.get(`${API}/strategy/departments`)
      ]);
      
      setPillars(pillarsRes.data);
      setGoals(goalsRes.data);
      setUsers(usersRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      console.error('Failed to fetch supporting data:', error);
    }
  }, [filters.year]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRocks(), fetchSupportingData()]);
      setLoading(false);
    };
    loadData();
  }, [fetchRocks, fetchSupportingData]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.year !== currentYear) params.set('year', filters.year);
    if (filters.quarter) params.set('quarter', filters.quarter);
    if (filters.pillar_id) params.set('pillar', filters.pillar_id);
    if (filters.status) params.set('status', filters.status);
    if (filters.owner_id) params.set('owner', filters.owner_id);
    setSearchParams(params);
  }, [filters, currentYear, setSearchParams]);

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      year: currentYear,
      quarter: '',
      pillar_id: '',
      status: '',
      owner_id: ''
    });
  };

  const hasActiveFilters = filters.quarter || filters.pillar_id || filters.status || filters.owner_id;

  // Get goals for selected pillar
  const getGoalsForPillar = (pillarId) => {
    return goals.filter(g => g.pillar_id === pillarId);
  };

  // === ROCK OPERATIONS ===
  const openRockDialog = (rock = null) => {
    if (rock) {
      setEditingRock(rock);
      setRockForm({
        name: rock.name,
        description: rock.description || '',
        owner_id: rock.owner_id,
        department: rock.department || '',
        pillar_id: rock.pillar_id,
        annual_goal_id: rock.annual_goal_id || '',
        year: rock.year,
        quarter: rock.quarter,
        status: rock.status,
        percent_complete: rock.percent_complete,
        risks_blockers: rock.risks_blockers || ''
      });
    } else {
      setEditingRock(null);
      setRockForm({
        name: '',
        description: '',
        owner_id: users[0]?.id || '',
        department: '',
        pillar_id: filters.pillar_id || (pillars[0]?.id || ''),
        annual_goal_id: '',
        year: filters.year,
        quarter: filters.quarter || currentQuarter,
        status: 'not_started',
        percent_complete: 0,
        risks_blockers: ''
      });
    }
    setRockDialogOpen(true);
  };

  const saveRock = async () => {
    if (!rockForm.name.trim()) {
      toast.error('Rock name is required');
      return;
    }
    if (!rockForm.owner_id) {
      toast.error('Please select an owner');
      return;
    }
    if (!rockForm.pillar_id) {
      toast.error('Please select a pillar');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...rockForm,
        year: parseInt(rockForm.year),
        percent_complete: parseInt(rockForm.percent_complete),
        annual_goal_id: rockForm.annual_goal_id || null
      };

      if (editingRock) {
        await axios.put(`${API}/strategy/rocks/${editingRock.id}`, payload);
        toast.success('Rock updated');
      } else {
        await axios.post(`${API}/strategy/rocks`, payload);
        toast.success('Rock created');
      }
      setRockDialogOpen(false);
      fetchRocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rock');
    } finally {
      setSaving(false);
    }
  };

  // Quick status update
  const openStatusDialog = (rock) => {
    setRockToUpdateStatus(rock);
    setStatusDialogOpen(true);
  };

  const updateRockStatus = async (newStatus, newPercent) => {
    if (!rockToUpdateStatus) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/strategy/rocks/${rockToUpdateStatus.id}`, {
        status: newStatus,
        percent_complete: newPercent
      });
      toast.success('Status updated');
      setStatusDialogOpen(false);
      setRockToUpdateStatus(null);
      fetchRocks();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  // Delete rock
  const openDeleteDialog = (rock) => {
    setRockToDelete(rock);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!rockToDelete) return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/strategy/rocks/${rockToDelete.id}`);
      toast.success('Rock deleted');
      setDeleteDialogOpen(false);
      setRockToDelete(null);
      fetchRocks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete rock');
    } finally {
      setSaving(false);
    }
  };

  const canManage = isAdmin() || isELT();

  // Calculate summary stats
  const stats = {
    total: rocks.length,
    onTrack: rocks.filter(r => r.status === 'green').length,
    atRisk: rocks.filter(r => r.status === 'yellow').length,
    offTrack: rocks.filter(r => r.status === 'red').length,
    completed: rocks.filter(r => r.status === 'completed').length,
    avgProgress: rocks.length > 0 
      ? Math.round(rocks.reduce((sum, r) => sum + r.percent_complete, 0) / rocks.length)
      : 0
  };

  if (loading) {
    return (
      <Layout>
        <Header title="Quarterly Rocks" />
        <div className="p-4 sm:p-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Quarterly Rocks" />
      
      <div className="p-4 sm:p-6 space-y-6" data-testid="quarterly-rocks">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Total Rocks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-green-600">{stats.onTrack}</p>
              <p className="text-xs text-slate-500">On Track</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-yellow-600">{stats.atRisk}</p>
              <p className="text-xs text-slate-500">At Risk</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-red-600">{stats.offTrack}</p>
              <p className="text-xs text-slate-500">Off Track</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-blue-600">{stats.completed}</p>
              <p className="text-xs text-slate-500">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-2xl font-bold text-slate-900">{stats.avgProgress}%</p>
              <p className="text-xs text-slate-500">Avg Progress</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Filter className="w-4 h-4" />
            Filters:
          </div>
          
          <Select value={filters.year.toString()} onValueChange={(v) => setFilters({ ...filters, year: parseInt(v) })}>
            <SelectTrigger className="w-24" data-testid="year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.quarter} onValueChange={(v) => setFilters({ ...filters, quarter: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-28" data-testid="quarter-filter">
              <SelectValue placeholder="Quarter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Quarters</SelectItem>
              {QUARTERS.map(q => (
                <SelectItem key={q} value={q}>{q}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.pillar_id} onValueChange={(v) => setFilters({ ...filters, pillar_id: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-40" data-testid="pillar-filter">
              <SelectValue placeholder="Pillar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pillars</SelectItem>
              {pillars.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                    {p.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-32" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${config.color}`} />
                    {config.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}

          <div className="ml-auto">
            {canManage && (
              <Button onClick={() => openRockDialog()} data-testid="add-rock-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Rock
              </Button>
            )}
          </div>
        </div>

        {/* Rocks List */}
        {rocks.length === 0 ? (
          <Card className="p-8 text-center">
            <Mountain className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">
              {hasActiveFilters 
                ? 'No rocks match your filters'
                : 'No quarterly rocks defined yet'
              }
            </p>
            {canManage && !hasActiveFilters && (
              <Button onClick={() => openRockDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Rock
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-3">
            {rocks.map(rock => {
              const statusConfig = STATUS_CONFIG[rock.status] || STATUS_CONFIG.not_started;
              
              return (
                <Card 
                  key={rock.id} 
                  className={`overflow-hidden border-l-4 ${statusConfig.bgColor}`}
                  style={{ borderLeftColor: statusConfig.color.replace('bg-', '').includes('slate') ? '#94a3b8' : undefined }}
                  data-testid={`rock-${rock.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Status indicator */}
                      <div className="flex flex-col items-center gap-1 pt-1">
                        <div className={`w-3 h-3 rounded-full ${statusConfig.color}`} />
                      </div>
                      
                      {/* Main content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-slate-900">{rock.name}</h3>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {rock.owner_name}
                              </span>
                              <span 
                                className="px-2 py-0.5 rounded text-xs"
                                style={{ 
                                  backgroundColor: rock.pillar_color + '20', 
                                  color: rock.pillar_color 
                                }}
                              >
                                {rock.pillar_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {rock.quarter} {rock.year}
                              </span>
                              {rock.department && (
                                <Badge variant="outline" className="text-xs">
                                  {rock.department}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {/* Progress and actions */}
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-lg font-semibold">{rock.percent_complete}%</p>
                              <p className={`text-xs ${statusConfig.textColor}`}>{statusConfig.label}</p>
                            </div>
                            
                            {canManage && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openStatusDialog(rock)}>
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                    Update Status
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openRockDialog(rock)}>
                                    <Edit2 className="w-4 h-4 mr-2" />
                                    Edit Rock
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={() => openDeleteDialog(rock)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Rock
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="mt-3">
                          <Progress value={rock.percent_complete} className="h-2" />
                        </div>
                        
                        {/* Description and milestones */}
                        {(rock.description || rock.milestones_total > 0 || rock.risks_blockers) && (
                          <div className="mt-3 space-y-2">
                            {rock.description && (
                              <p className="text-sm text-slate-600">{rock.description}</p>
                            )}
                            
                            <div className="flex flex-wrap gap-4 text-sm">
                              {rock.milestones_total > 0 && (
                                <span className="text-slate-500">
                                  Milestones: {rock.milestones_completed}/{rock.milestones_total}
                                </span>
                              )}
                              {rock.annual_goal_title && (
                                <span className="text-slate-500">
                                  Goal: {rock.annual_goal_title}
                                </span>
                              )}
                            </div>
                            
                            {rock.risks_blockers && (
                              <div className="flex items-start gap-2 p-2 bg-red-50 rounded text-sm">
                                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <span className="text-red-700">{rock.risks_blockers}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Rock Dialog */}
      <Dialog open={rockDialogOpen} onOpenChange={setRockDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRock ? 'Edit Rock' : 'Create Quarterly Rock'}</DialogTitle>
            <DialogDescription>
              Rocks are the key initiatives that drive quarterly execution of your strategy.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rock-name">Name *</Label>
              <Input
                id="rock-name"
                value={rockForm.name}
                onChange={(e) => setRockForm({ ...rockForm, name: e.target.value })}
                placeholder="e.g., Launch Youth Sports Product"
                data-testid="rock-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rock-owner">Owner *</Label>
                <Select 
                  value={rockForm.owner_id} 
                  onValueChange={(v) => setRockForm({ ...rockForm, owner_id: v })}
                >
                  <SelectTrigger data-testid="rock-owner-select">
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="rock-department">Department</Label>
                <Select 
                  value={rockForm.department} 
                  onValueChange={(v) => setRockForm({ ...rockForm, department: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rock-pillar">Strategic Pillar *</Label>
                <Select 
                  value={rockForm.pillar_id} 
                  onValueChange={(v) => setRockForm({ ...rockForm, pillar_id: v, annual_goal_id: '' })}
                >
                  <SelectTrigger data-testid="rock-pillar-select">
                    <SelectValue placeholder="Select pillar" />
                  </SelectTrigger>
                  <SelectContent>
                    {pillars.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                          {p.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="rock-goal">Annual Goal (Optional)</Label>
                <Select 
                  value={rockForm.annual_goal_id || 'none'} 
                  onValueChange={(v) => setRockForm({ ...rockForm, annual_goal_id: v === 'none' ? '' : v })}
                  disabled={!rockForm.pillar_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific goal</SelectItem>
                    {getGoalsForPillar(rockForm.pillar_id).map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rock-year">Year *</Label>
                <Select 
                  value={rockForm.year.toString()} 
                  onValueChange={(v) => setRockForm({ ...rockForm, year: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="rock-quarter">Quarter *</Label>
                <Select 
                  value={rockForm.quarter} 
                  onValueChange={(v) => setRockForm({ ...rockForm, quarter: v })}
                >
                  <SelectTrigger data-testid="rock-quarter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUARTERS.map(q => (
                      <SelectItem key={q} value={q}>{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="rock-description">Description</Label>
              <Textarea
                id="rock-description"
                value={rockForm.description}
                onChange={(e) => setRockForm({ ...rockForm, description: e.target.value })}
                placeholder="Describe this rock's objectives..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rock-status">Status</Label>
                <Select 
                  value={rockForm.status} 
                  onValueChange={(v) => setRockForm({ ...rockForm, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${config.color}`} />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="rock-progress">Progress ({rockForm.percent_complete}%)</Label>
                <Input
                  id="rock-progress"
                  type="range"
                  min="0"
                  max="100"
                  value={rockForm.percent_complete}
                  onChange={(e) => setRockForm({ ...rockForm, percent_complete: parseInt(e.target.value) })}
                  className="h-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="rock-risks">Risks / Blockers</Label>
              <Textarea
                id="rock-risks"
                value={rockForm.risks_blockers}
                onChange={(e) => setRockForm({ ...rockForm, risks_blockers: e.target.value })}
                placeholder="Any risks or blockers that might impact delivery..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRockDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveRock} disabled={saving} data-testid="save-rock-btn">
              {saving ? 'Saving...' : (editingRock ? 'Update Rock' : 'Create Rock')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Update Rock Status</DialogTitle>
            <DialogDescription>
              {rockToUpdateStatus?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <Button
                  key={key}
                  variant="outline"
                  className={`justify-start ${rockToUpdateStatus?.status === key ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => updateRockStatus(key, key === 'completed' ? 100 : rockToUpdateStatus?.percent_complete)}
                  disabled={saving}
                >
                  <div className={`w-3 h-3 rounded-full ${config.color} mr-2`} />
                  {config.label}
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Rock</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{rockToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={saving}
              data-testid="confirm-delete-rock-btn"
            >
              {saving ? 'Deleting...' : 'Delete Rock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default QuarterlyRocks;
